import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/projeto.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const ProjetoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [errosValidacao, setErrosValidacao] = useState({});
  const [formData, setFormData] = useState({
    id_projeto: '',
    id_escola: '',
    sei: '',
    id_tp_projeto: '',
    data_inicio: '',
    id_gerencia: '',
    id_status: '',
    descr_projeto: '',
    id_modalidade: '',
    data_termino: '',
    id_solicitacao: '',
    id_tp_regularizacao: '',
    id_acao: '',
    valor_projeto: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [tiposProjeto, setTiposProjeto] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [status, setStatus] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [tiposRegularizacao, setTiposRegularizacao] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [proximoId, setProximoId] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposProjeto = useCallback(async () => {
    try {
      const response = await api.get('/tipo-projeto');
      setTiposProjeto(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de projeto: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchGerencias = useCallback(async () => {
    try {
      const response = await api.get('/gerencia');
      setGerencias(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar gerências: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/status');
      setStatus(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar status: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchModalidades = useCallback(async () => {
    try {
      const response = await api.get('/tipo-modalidade');
      setModalidades(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar modalidades: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchSolicitacoes = useCallback(async () => {
    try {
      const response = await api.get('/solicitacao');
      setSolicitacoes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar solicitações: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposRegularizacao = useCallback(async () => {
    try {
      const response = await api.get('/tipo-regularizacao');
      setTiposRegularizacao(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de regularização: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchAcoes = useCallback(async () => {
    try {
      const response = await api.get('/acao');
      setAcoes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar ações: ${error.message}`);
    }
  }, [mostrarMensagem]);

  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/projeto');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    fetchTiposProjeto();
    fetchGerencias();
    fetchStatus();
    fetchModalidades();
    fetchSolicitacoes();
    fetchTiposRegularizacao();
    fetchAcoes();
    fetchNextId();
  }, [verificarAPI, fetchEscolas, fetchTiposProjeto, fetchGerencias, fetchStatus, fetchModalidades, fetchSolicitacoes, fetchTiposRegularizacao, fetchAcoes, fetchNextId]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'projeto', { id, dadosAtualizacao });
    try {
      const { data_inicio, data_termino } = dadosAtualizacao;

      // Validar datas (Inicio deve ser menor que Termino)
      if (data_inicio && data_termino) {
        const inicio = new Date(data_inicio);
        const termino = new Date(data_termino);
        if (inicio >= termino) {
          mostrarMensagem('error', 'A Data de Início deve ser anterior à Data de Término.');
          return;
        }
      }

      const validKeys = [
        'id_escola', 'sei', 'id_tp_projeto', 'data_inicio', 'id_gerencia', 'id_status',
        'descr_projeto', 'id_modalidade', 'data_termino', 'id_solicitacao',
        'id_tp_regularizacao', 'id_acao', 'valor_projeto'
      ];

      const dadosValidos = {};

      for (const key of validKeys) {
        if (dadosAtualizacao.hasOwnProperty(key)) {
          if (key === 'valor_projeto') {
            const valorString = String(dadosAtualizacao[key]);
            if (valorString === '' || dadosAtualizacao[key] === null || valorString === 'null') {
              dadosValidos[key] = null;
            } else if (valorString.includes(',')) {
              // Valor formatado brasileiro (ex: 1.000,00)
              dadosValidos[key] = parseFloat(valorString.replace(/\./g, '').replace(',', '.'));
            } else {
              // Valor simples
              dadosValidos[key] = parseFloat(dadosAtualizacao[key]);
            }
          } else {
            dadosValidos[key] = dadosAtualizacao[key] === '' ? null : dadosAtualizacao[key];
          }
        }
      }

      const response = await api.put(`/projeto/${id}`, dadosValidos);
      logarSucesso('Atualizar', 'projeto', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'projeto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'projeto', { id });
    try {
      const response = await api.delete(`/projeto/${id}`);
      logarSucesso('Excluir', 'projeto', response.data);
      setDados(currentData => currentData.filter(item => item.id_projeto !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'projeto');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, escola) => {
      acc[escola.id_escola] = escola.nome_escola;
      return acc;
    }, {});

    const tiposProjetoNomes = tiposProjeto.reduce((acc, tipo) => {
      acc[tipo.id_tp_projeto] = tipo.descr_tp_projeto;
      return acc;
    }, {});

    const gerenciasNomes = gerencias.reduce((acc, gerencia) => {
      acc[gerencia.id_gerencia] = gerencia.nome_gerencia;
      return acc;
    }, {});

    const statusNomes = status.reduce((acc, s) => {
      acc[s.id_status] = s.tipo_status;
      return acc;
    }, {});

    const statusCores = status.reduce((acc, s) => {
      acc[s.id_status] = s.cor;
      return acc;
    }, {});

    const modalidadesNomes = modalidades.reduce((acc, modalidade) => {
      acc[modalidade.id_tp_modalidade] = modalidade.descr_modalidade;
      return acc;
    }, {});

    const solicitacoesNomes = solicitacoes.reduce((acc, solicitacao) => {
      acc[solicitacao.id_solicitacao] = solicitacao.descr_solicitacao;
      return acc;
    }, {});

    const tiposRegularizacaoNomes = tiposRegularizacao.reduce((acc, tipo) => {
      acc[tipo.id_tp_regularizacao] = tipo.descricao;
      return acc;
    }, {});

    const acoesNomes = acoes.reduce((acc, acao) => {
      acc[acao.id_acao] = acao.nome;
      return acc;
    }, {});

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_projeto, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_projeto}"?`)) {
            excluirRegistro(data.id_projeto);
          }
        },
        headerSort: false
      },
      {
        title: 'ID Projeto',
        field: 'id_projeto',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        frozen: true,
        sorter: 'string'
      },
      {
        title: 'Nome Escola',
        field: 'id_escola',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar escola...',
        sorter: 'string',
        formatter: (cell) => {
          return escolasNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeEscola = escolasNomes[rowValue] || rowValue || '';
          return nomeEscola.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: escolasNomes
        }
      },
      {
        title: 'SEI',
        field: 'sei',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar SEI...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Tipo Projeto',
        field: 'id_tp_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar tipo projeto...',
        sorter: 'string',
        formatter: (cell) => {
          return tiposProjetoNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeTipoProjeto = tiposProjetoNomes[rowValue] || rowValue || '';
          return nomeTipoProjeto.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: tiposProjetoNomes
        }
      },
      {
        title: 'Gerência',
        field: 'id_gerencia',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar gerência...',
        sorter: 'string',
        formatter: (cell) => {
          return gerenciasNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeGerencia = gerenciasNomes[rowValue] || rowValue || '';
          return nomeGerencia.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: gerenciasNomes
        }
      },
      {
        title: 'Data Início',
        field: 'data_inicio',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        width: 150,
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Data Término',
        field: 'data_termino',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        width: 180,
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Status',
        field: 'id_status',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar status...',
        sorter: 'string',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeStatus = statusNomes[rowValue] || rowValue || '';
          return nomeStatus.toLowerCase().includes(headerValue.toLowerCase());
        },
        formatter: (cell) => {
          const statusId = cell.getValue();
          const color = statusCores[statusId];
          const text = statusNomes[statusId] || statusId;

          if (!color) return text;

          const getTextColor = (bgColor) => {
            if (!bgColor) return '#000000';
            const color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor;
            const r = parseInt(color.substring(0, 2), 16);
            const g = parseInt(color.substring(2, 4), 16);
            const b = parseInt(color.substring(4, 6), 16);
            const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
            return brightness > 155 ? '#000000' : '#FFFFFF';
          };

          const textColor = getTextColor(color);
          const span = document.createElement('span');
          span.style.backgroundColor = color;
          span.style.color = textColor;
          span.style.padding = '2px 8px';
          span.style.borderRadius = '12px';
          span.style.fontWeight = 'bold';
          span.style.display = 'inline-block';
          span.textContent = text;

          return span;
        },
        editor: 'list',
        editorParams: {
          values: statusNomes
        }
      },
      {
        title: 'Descrição',
        field: 'descr_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Modalidade',
        field: 'id_modalidade',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar modalidade...',
        sorter: 'string',
        formatter: (cell) => {
          return modalidadesNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeModalidade = modalidadesNomes[rowValue] || rowValue || '';
          return nomeModalidade.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: modalidadesNomes
        }
      },
      {
        title: 'Solicitação',
        field: 'id_solicitacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar solicitação...',
        sorter: 'string',
        formatter: (cell) => {
          return solicitacoesNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeSolicitacao = solicitacoesNomes[rowValue] || rowValue || '';
          return nomeSolicitacao.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: solicitacoesNomes
        }
      },
      {
        title: 'Tipo Regularização',
        field: 'id_tp_regularizacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar regularização...',
        sorter: 'string',
        formatter: (cell) => {
          return tiposRegularizacaoNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeTipoRegularizacao = tiposRegularizacaoNomes[rowValue] || rowValue || '';
          return nomeTipoRegularizacao.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: tiposRegularizacaoNomes
        }
      },
      {
        title: 'Ação',
        field: 'id_acao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ação...',
        sorter: 'string',
        formatter: (cell) => {
          return acoesNomes[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeAcao = acoesNomes[rowValue] || rowValue || '';
          return nomeAcao.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: acoesNomes
        }
      },
      {
        title: 'Valor do Projeto',
        field: 'valor_projeto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar valor...',
        sorter: 'number',
        formatter: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined || value === '') {
            return '';
          }
          return parseFloat(value).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = parseFloat(rowValue).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/[^\d,]/g, '').replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: function (cell, onRendered, success, cancel) {
          const input = document.createElement('input');
          input.type = 'text';
          input.style.width = '100%';
          input.style.padding = '4px';
          input.style.boxSizing = 'border-box';

          const currentValue = cell.getValue();
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            input.value = parseFloat(currentValue).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }

          input.addEventListener('input', (e) => {
            let value = e.target.value.replace(/\D/g, '');
            if (value) {
              value = (parseInt(value) / 100).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              e.target.value = value;
            }
          });

          onRendered(() => {
            input.focus();
            input.select();
          });

          const successFunc = () => {
            const rawValue = input.value.replace(/\./g, '').replace(',', '.');
            success(parseFloat(rawValue) || 0);
          };

          input.addEventListener('blur', successFunc);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              successFunc();
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      }
    ];
  }, [escolas, tiposProjeto, gerencias, status, modalidades, solicitacoes, tiposRegularizacao, acoes, atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/projeto');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_projeto'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_escola, sei, id_tp_projeto, data_inicio, id_gerencia, id_status, descr_projeto, id_modalidade, data_termino, id_solicitacao, id_tp_regularizacao, id_acao, valor_projeto } = formData;

    // Validar campos obrigatórios (ID será gerado automaticamente)
    const novosErros = {};
    const camposFaltando = [];

    if (!id_escola || id_escola === '') {
      novosErros.id_escola = true;
      camposFaltando.push('Nome Escola');
    }
    if (!descr_projeto || descr_projeto.trim() === '') {
      novosErros.descr_projeto = true;
      camposFaltando.push('Descrição');
    }
    if (!id_tp_projeto || id_tp_projeto === '') {
      novosErros.id_tp_projeto = true;
      camposFaltando.push('Tipo Projeto');
    }
    if (!id_gerencia || id_gerencia === '') {
      novosErros.id_gerencia = true;
      camposFaltando.push('Gerência');
    }

    if (Object.keys(novosErros).length > 0) {
      setErrosValidacao(novosErros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    // Validar datas (Inicio deve ser menor que Termino)
    if (data_inicio && data_termino) {
      const inicio = new Date(data_inicio);
      const termino = new Date(data_termino);
      if (inicio >= termino) {
        mostrarMensagem('error', 'A Data de Início deve ser anterior à Data de Término.');
        return;
      }
    }

    // Limpar erros se passou na validação
    setErrosValidacao({});

    const dataToSend = {
      id_projeto: 'AUTO', // ID será gerado automaticamente pelo backend
      id_escola,
      sei: sei === '' ? null : sei,
      id_tp_projeto,
      data_inicio: data_inicio === '' ? null : data_inicio,
      id_gerencia: id_gerencia === '' ? null : id_gerencia,
      id_status: id_status === '' ? null : id_status,
      descr_projeto: descr_projeto === '' ? null : descr_projeto,
      id_modalidade: id_modalidade === '' ? null : id_modalidade,
      data_termino: data_termino === '' ? null : data_termino,
      id_solicitacao: id_solicitacao === '' ? null : id_solicitacao,
      id_tp_regularizacao: id_tp_regularizacao === '' ? null : id_tp_regularizacao,
      id_acao: id_acao === '' ? null : id_acao,
      valor_projeto: valor_projeto === '' ? null : parseFloat(valor_projeto.replace(/\./g, '').replace(',', '.'))
    };

    logarRequisicao('Adicionar', 'projeto', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/projeto', dataToSend);
      logarSucesso('Adicionar', 'projeto', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_projeto: '', id_escola: '', sei: '', id_tp_projeto: '', data_inicio: '', id_gerencia: '', id_status: '', descr_projeto: '', id_modalidade: '', data_termino: '', id_solicitacao: '', id_tp_regularizacao: '', id_acao: '', valor_projeto: '' });
      setErrosValidacao({});
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      fetchNextId();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'projeto');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Formatação especial para valor_projeto
    if (name === 'valor_projeto') {
      // Remove tudo que não é número
      const apenasNumeros = value.replace(/\D/g, '');

      // Se não tem números, limpa o campo
      if (apenasNumeros === '') {
        setFormData(prev => ({ ...prev, [name]: '' }));
        return;
      }

      // Converte para número e formata com pontos e vírgula
      const numero = parseInt(apenasNumeros, 10);
      const valorFormatado = (numero / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      setFormData(prev => ({ ...prev, [name]: valorFormatado }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    // Limpar erro do campo quando o usuário começar a preencher
    if (errosValidacao[name]) {
      setErrosValidacao(prev => {
        const novosErros = { ...prev };
        delete novosErros[name];
        return novosErros;
      });
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'projetos.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Projetos</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_projeto</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/projeto.mov" buttonText="Ver Tutorial Projeto" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group third-width">
            <label htmlFor="id_projeto">ID Projeto:</label>
            <input
              type="text"
              id="id_projeto"
              name="id_projeto"
              value=""
              placeholder="Gerado ao adicionar"
              disabled={true}
              style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }}
            />
          </div>
          <div className="form-group third-width">
            <label htmlFor="id_escola">Escola: <span className="required-asterisk">*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                // Buscar escola pelo formato "Nome (INEP)" ou apenas pelo nome
                const escolaEncontrada = escolas.find(esc => {
                  const formato = `${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})`;
                  return formato === e.target.value || esc.nome_escola === e.target.value;
                });
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              className={errosValidacao.id_escola ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="escolas-list">
              {escolasFiltradas.map(escola => (
                <option key={escola.id_escola} value={`${escola.nome_escola} (${escola.codigo_inep || escola.id_escola})`} />
              ))}
            </datalist>
          </div>
          <div className="form-group third-width">
            <label htmlFor="sei">SEI:</label>
            <input
              type="number"
              id="sei"
              name="sei"
              value={formData.sei}
              onChange={handleInputChange}
              placeholder="Ex: 12345"
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-group full-width">
            <label htmlFor="descr_projeto">Descrição: <span className="required-asterisk">*</span></label>
            <input
              type="text"
              id="descr_projeto"
              name="descr_projeto"
              value={formData.descr_projeto}
              onChange={handleInputChange}
              placeholder="Ex: Reforma da quadra"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={errosValidacao.descr_projeto ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_projeto">Tipo projeto: <span className="required-asterisk">*</span></label>
            <select
              id="id_tp_projeto"
              name="id_tp_projeto"
              value={formData.id_tp_projeto}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={errosValidacao.id_tp_projeto ? 'input-error' : ''}
            >
              <option value="">Selecione um tipo de projeto</option>
              {tiposProjeto.map(tipo => (
                <option key={tipo.id_tp_projeto} value={tipo.id_tp_projeto}>{tipo.descr_tp_projeto}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="id_gerencia">Gerência: <span className="required-asterisk">*</span></label>
            <select
              id="id_gerencia"
              name="id_gerencia"
              value={formData.id_gerencia}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={errosValidacao.id_gerencia ? 'input-error' : ''}
            >
              <option value="">Selecione uma gerência</option>
              {gerencias.map(gerencia => (
                <option key={gerencia.id_gerencia} value={gerencia.id_gerencia}>{gerencia.nome_gerencia}</option>
              ))}
            </select>
          </div>
          <div className="form-group half-width">
            <label htmlFor="data_inicio">Data início:</label>
            <input type="date" id="data_inicio" name="data_inicio" value={formData.data_inicio} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group half-width">
            <label htmlFor="data_termino">Data término:</label>
            <input type="date" id="data_termino" name="data_termino" value={formData.data_termino} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_status">Status:</label>
            <select id="id_status" name="id_status" value={formData.id_status} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione um status</option>
              {status.map(s => (
                <option key={s.id_status} value={s.id_status}>{s.tipo_status}</option>
              ))}
            </select>
          </div>
          <div className="form-group fifth-width">
            <label htmlFor="id_modalidade">Modalidade:</label>
            <select id="id_modalidade" name="id_modalidade" value={formData.id_modalidade} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione uma modalidade</option>
              {modalidades.map(modalidade => (
                <option key={modalidade.id_tp_modalidade} value={modalidade.id_tp_modalidade}>{modalidade.descr_modalidade}</option>
              ))}
            </select>
          </div>
          <div className="form-group fifth-width">
            <label htmlFor="id_solicitacao">Solicitação:</label>
            <select id="id_solicitacao" name="id_solicitacao" value={formData.id_solicitacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione uma solicitação</option>
              {solicitacoes.map(solicitacao => (
                <option key={solicitacao.id_solicitacao} value={solicitacao.id_solicitacao}>{solicitacao.descr_solicitacao}</option>
              ))}
            </select>
          </div>
          <div className="form-group fifth-width">
            <label htmlFor="id_tp_regularizacao">Tipo regularização:</label>
            <select id="id_tp_regularizacao" name="id_tp_regularizacao" value={formData.id_tp_regularizacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione um tipo</option>
              {tiposRegularizacao.map(tipo => (
                <option key={tipo.id_tp_regularizacao} value={tipo.id_tp_regularizacao}>{tipo.descricao}</option>
              ))}
            </select>
          </div>
          <div className="form-group fifth-width">
            <label htmlFor="id_acao">Ação:</label>
            <select id="id_acao" name="id_acao" value={formData.id_acao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione uma ação</option>
              {acoes.map(acao => (
                <option key={acao.id_acao} value={acao.id_acao}>{acao.nome}</option>
              ))}
            </select>
          </div>
          <div className="form-group fifth-width">
            <label htmlFor="valor_projeto">Valor do projeto:</label>
            <input
              type="text"
              id="valor_projeto"
              name="valor_projeto"
              value={formData.valor_projeto}
              onChange={handleInputChange}
              placeholder="Ex: 5000,00"
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default ProjetoManager;
